// "ComeFit Belege" - Upload-Seite fuer das Smartphone (Route /belege-upload). Extrem einfach und AUF UPLOAD BESCHRAENKT:
//   Foto aufnehmen | Datei/PDF auswaehlen | mehrere Fotos zu einem Beleg | Upload-Status mit Belegnummer.
// Keine Anmeldung, kein Finanzmodul, keine Beleganzeige. Der Zugang wird einmalig per Einladungs-Link/QR-Code eingerichtet (siehe Verwaltung);
// danach gilt ein geraetegebundenes, widerrufbares Cookie. Alle Berechtigungen prueft der Server.
// Offline: Aufnahmen bleiben auf dem Geraet (IndexedDB) und werden automatisch nachgesendet; "übermittelt" gilt erst mit Belegnummer vom Server.
import { useState, useEffect, useRef, useCallback } from "react";
import THEME from "../../theme";
import { API, NetzFehler } from "./belegeApi";
import { uploadApi } from "./uploadApi";

// ---------------------------------------------------------------- lokale Warteschlange (IndexedDB)
const DB_NAME = "comefit-belege-upload", STORE = "warteschlange";
function idb() {
  return new Promise((ok, fehl) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fehl(r.error);
  });
}
const idbOp = async (modus, fn) => { const d = await idb(); return new Promise((ok, f) => { const q = fn(d.transaction(STORE, modus).objectStore(STORE)); q.onsuccess = () => ok(q.result); q.onerror = () => f(q.error); }); };
const queueAlle = () => idbOp("readonly", s => s.getAll());
const queueAdd = (e) => idbOp("readwrite", s => s.add(e));
const queueDel = (id) => idbOp("readwrite", s => s.delete(id));

const formDataAus = (seiten, quelle) => {
  const fd = new FormData();
  seiten.forEach((p, i) => fd.append("files", p.blob, p.name || `seite-${i + 1}.jpg`));
  fd.append("quelle", quelle);
  return fd;
};

const ersteSeite = { minHeight: "100vh", background: THEME.colors.background, color: THEME.colors.text, boxSizing: "border-box",
  padding: "max(18px, env(safe-area-inset-top)) 18px max(28px, env(safe-area-inset-bottom))" };
const grossBtn = { width: "100%", minHeight: 104, fontSize: 24, fontWeight: 700, borderRadius: 22, border: "none", color: THEME.colors.text,
  display: "flex", alignItems: "center", justifyContent: "center", gap: 14, cursor: "pointer", boxSizing: "border-box" };
const miniBtn = { width: 44, height: 44, borderRadius: 12, border: "none", background: THEME.colors.surfaceMuted, color: THEME.colors.text, fontSize: 20, cursor: "pointer" };
const zeitText = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function BelegeUploadApp() {
  const einladung = new URLSearchParams(window.location.search).get("e");
  const istApp = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const [phase, setPhase] = useState("laedt");       // laedt | einrichten | bereit | nicht_eingerichtet | inaktiv
  const [bezeichnung, setBezeichnung] = useState("");
  const [fehlerText, setFehlerText] = useState("");
  const [seiten, setSeiten] = useState([]);
  const [sendet, setSendet] = useState(false);
  const [fortschritt, setFortschritt] = useState(0);
  const [ergebnis, setErgebnis] = useState(null);    // {typ: ok|dublette|fehler|wartet, text, nr}
  const [wartend, setWartend] = useState(0);
  const [letzte, setLetzte] = useState([]);
  const kameraRef = useRef(null);
  const dateiRef = useRef(null);
  const zaehler = useRef(0);

  // eigenes Manifest = eigener Startbildschirm-Button "ComeFit Belege"; mit Einladung, damit sich die installierte App selbst einrichten kann
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", uploadApi.manifestUrl(einladung));
    document.title = "ComeFit Belege";
  }, [einladung]);

  const letzteLaden = useCallback(() => { uploadApi.meine().then(setLetzte).catch(() => {}); }, []);
  const wartendZaehlen = useCallback(() => { queueAlle().then(q => setWartend(q.length)).catch(() => {}); }, []);

  const einrichtenJetzt = useCallback(async () => {
    setFehlerText("");
    try {
      const r = await uploadApi.einrichten(einladung);
      setBezeichnung(r.bezeichnung);
      window.history.replaceState(null, "", "/belege-upload");   // Einladung aus der Adresszeile entfernen
      setPhase("bereit");
    } catch (e) {
      setFehlerText(e instanceof NetzFehler ? "Keine Verbindung - bitte später erneut versuchen." : (e.message || "Einrichtung nicht möglich."));
      setPhase("nicht_eingerichtet");
    }
  }, [einladung]);

  useEffect(() => {
    uploadApi.status().then(s => {
      if (!s.aktiv) { setPhase("inaktiv"); return; }
      if (s.eingerichtet) { setBezeichnung(s.bezeichnung); if (einladung) window.history.replaceState(null, "", "/belege-upload"); setPhase("bereit"); return; }
      if (einladung && istApp) { einrichtenJetzt(); return; }     // installierte App: sofort einrichten
      setPhase(einladung ? "einrichten" : "nicht_eingerichtet");   // im Browser: erst App installieren (Hinweis), sonst "im Browser einrichten"
    }).catch(() => setPhase("nicht_eingerichtet"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const warteschlangeSenden = useCallback(async () => {
    let q = [];
    try { q = await queueAlle(); } catch { return; }
    for (const e of q) {
      try {
        const r = await uploadApi.hochladen(formDataAus(e.seiten, e.quelle));
        await queueDel(e.id);
        setErgebnis({ typ: r.dublette ? "dublette" : "ok", nr: r.beleg_nr, text: r.dublette ? (r.hinweis || "Bereits übermittelt.") : "Nachgesendet" });
      } catch (err) {
        if (err instanceof NetzFehler) break;
        if (err.status === 401) { setPhase("nicht_eingerichtet"); setFehlerText("Dieser Zugang ist nicht mehr gültig."); return; }
        await queueDel(e.id);
        setErgebnis({ typ: "fehler", text: `Ein wartender Beleg wurde abgelehnt: ${err.message}` });
      }
    }
    wartendZaehlen(); letzteLaden();
  }, [wartendZaehlen, letzteLaden]);

  useEffect(() => {
    if (phase !== "bereit") return;
    wartendZaehlen(); letzteLaden(); warteschlangeSenden();
    window.addEventListener("online", warteschlangeSenden);
    return () => window.removeEventListener("online", warteschlangeSenden);
  }, [phase, warteschlangeSenden, wartendZaehlen, letzteLaden]);

  const hinzu = (liste, quelle) => {
    const neu = Array.from(liste || []).map(f => ({ id: ++zaehler.current, blob: f, name: f.name, quelle, vorschau: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
    if (neu.length) { setSeiten(s => [...s, ...neu]); setErgebnis(null); }
  };
  const entfernen = (id) => setSeiten(s => { const x = s.find(p => p.id === id); if (x?.vorschau) URL.revokeObjectURL(x.vorschau); return s.filter(p => p.id !== id); });
  const verschieben = (id, d) => setSeiten(s => { const i = s.findIndex(p => p.id === id), j = i + d; if (j < 0 || j >= s.length) return s; const k = [...s]; [k[i], k[j]] = [k[j], k[i]]; return k; });
  const leeren = () => { seiten.forEach(p => p.vorschau && URL.revokeObjectURL(p.vorschau)); setSeiten([]); };

  const senden = async () => {
    if (!seiten.length || sendet) return;
    setSendet(true); setFortschritt(0); setErgebnis(null);
    const quelle = seiten.every(p => p.quelle === "kamera") ? "kamera" : "upload";
    try {
      const r = await uploadApi.hochladen(formDataAus(seiten, quelle), setFortschritt);
      setErgebnis(r.dublette ? { typ: "dublette", nr: r.beleg_nr, text: r.hinweis || "Dieser Beleg liegt bereits vor." } : { typ: "ok", nr: r.beleg_nr, text: `${r.seiten} Seite${r.seiten === 1 ? "" : "n"} übermittelt` });
      leeren(); letzteLaden();
    } catch (err) {
      if (err instanceof NetzFehler) {
        try {
          await queueAdd({ seiten: seiten.map(p => ({ blob: p.blob, name: p.name })), quelle, ts: Date.now() });
          setErgebnis({ typ: "wartet", text: "Kein Netz - der Beleg wartet auf diesem Handy und wird automatisch gesendet, sobald du wieder online bist." });
          leeren(); wartendZaehlen();
        } catch { setErgebnis({ typ: "fehler", text: "Kein Netz und Speichern auf dem Handy nicht möglich. Bitte später erneut senden." }); }
      } else if (err.status === 401) {
        setPhase("nicht_eingerichtet"); setFehlerText("Dieser Zugang ist nicht mehr gültig. Bitte bei ComeFit neu einrichten lassen.");
      } else {
        setErgebnis({ typ: "fehler", text: err.message });
      }
    }
    setSendet(false);
  };

  const kopf = (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
      <img src={`${API}/einstellungen/logo`} alt="" style={{ height: 38 }} />
      <h1 style={{ fontSize: 24, margin: 0 }}>ComeFit Belege</h1>
    </div>
  );
  const zentriert = { ...ersteSeite, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" };

  if (phase === "laedt") return <div style={zentriert}>Lädt…</div>;
  if (phase === "inaktiv") return <div style={zentriert}><img src={`${API}/einstellungen/logo`} alt="" style={{ width: 150, marginBottom: 20 }} /><p>Der Beleg-Upload ist im Moment nicht aktiv.</p></div>;
  if (phase === "einrichten") {
    return (
      <div style={{ ...ersteSeite }}><div style={{ maxWidth: 480, margin: "0 auto" }}>
        {kopf}
        <div style={{ background: THEME.colors.surface, borderRadius: 18, padding: 18, lineHeight: 1.5 }}>
          <strong>Einrichtung dieses Handys</strong>
          <ol style={{ paddingLeft: 20 }}>
            <li><b>iPhone:</b> unten auf „Teilen“ tippen → „Zum Home-Bildschirm“. <b>Android:</b> Menü ⋮ → „App installieren“ bzw. „Zum Startbildschirm hinzufügen“.</li>
            <li>Danach die neue App <b>„ComeFit Belege“</b> auf dem Startbildschirm öffnen – sie richtet sich beim ersten Start selbst ein.</li>
          </ol>
          <p style={{ color: THEME.colors.textSecondary, fontSize: 15 }}>Der Link ist nur einmal und nur kurze Zeit gültig.</p>
        </div>
        <button onClick={einrichtenJetzt} style={{ ...grossBtn, minHeight: 64, fontSize: 18, background: THEME.colors.surfaceMuted, marginTop: 16 }}>Ohne App direkt im Browser einrichten</button>
        {fehlerText && <p style={{ color: THEME.colors.danger }}>{fehlerText}</p>}
      </div></div>
    );
  }
  if (phase === "nicht_eingerichtet") {
    return (
      <div style={zentriert}>
        <img src={`${API}/einstellungen/logo`} alt="" style={{ width: 150, marginBottom: 20 }} />
        <p style={{ maxWidth: 360, fontSize: 18 }}>Dieses Handy ist noch nicht für den Beleg-Upload eingerichtet.</p>
        <p style={{ maxWidth: 360, color: THEME.colors.textSecondary }}>Bitte den Einrichtungs-Link bzw. QR-Code von ComeFit verwenden.</p>
        {fehlerText && <p style={{ color: THEME.colors.danger, maxWidth: 360 }}>{fehlerText}</p>}
      </div>
    );
  }

  const farbe = { ok: THEME.colors.success, dublette: THEME.colors.warning, wartet: THEME.colors.warning, fehler: THEME.colors.danger };
  return (
    <div style={ersteSeite}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        {kopf}
        <input ref={kameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { hinzu(e.target.files, "kamera"); e.target.value = ""; }} />
        <input ref={dateiRef} type="file" accept="image/*,.heic,.heif,application/pdf" multiple style={{ display: "none" }} onChange={e => { hinzu(e.target.files, "upload"); e.target.value = ""; }} />
        <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
          <button style={{ ...grossBtn, background: THEME.colors.primary }} onClick={() => kameraRef.current?.click()}><span style={{ fontSize: 38 }}>📷</span> Foto aufnehmen</button>
          <button style={{ ...grossBtn, background: THEME.colors.surfaceMuted }} onClick={() => dateiRef.current?.click()}><span style={{ fontSize: 38 }}>📎</span> Datei auswählen</button>
        </div>

        {seiten.length > 0 && (
          <div style={{ background: THEME.colors.surface, borderRadius: 20, padding: 14, marginBottom: 16 }}>
            <div style={{ marginBottom: 6, fontWeight: 700, fontSize: 18 }}>{seiten.length} Seite{seiten.length === 1 ? "" : "n"} = 1 Beleg</div>
            <div style={{ color: THEME.colors.textSecondary, fontSize: 15, marginBottom: 10 }}>Weitere Seite? Oben nochmal „Foto aufnehmen“ tippen.</div>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}>
              {seiten.map((p, i) => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {p.vorschau ? <img src={p.vorschau} alt={`Seite ${i + 1}`} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10 }} />
                    : <div style={{ width: 64, height: 64, borderRadius: 10, background: THEME.colors.surfaceMuted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>📄</div>}
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Seite {i + 1}</div>
                  <button aria-label="nach oben" disabled={i === 0} onClick={() => verschieben(p.id, -1)} style={miniBtn}>↑</button>
                  <button aria-label="nach unten" disabled={i === seiten.length - 1} onClick={() => verschieben(p.id, 1)} style={miniBtn}>↓</button>
                  <button aria-label="entfernen" onClick={() => entfernen(p.id)} style={{ ...miniBtn, color: THEME.colors.danger }}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={senden} disabled={sendet} style={{ ...grossBtn, minHeight: 72, fontSize: 22, background: THEME.colors.success, marginTop: 14, opacity: sendet ? 0.7 : 1 }}>
              {sendet ? `Wird gesendet… ${fortschritt}%` : "Beleg senden"}
            </button>
          </div>
        )}

        {ergebnis && (
          <div role="status" style={{ border: `2px solid ${farbe[ergebnis.typ]}`, borderRadius: 18, padding: "16px 16px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 40 }}>{ergebnis.typ === "ok" ? "✅" : ergebnis.typ === "fehler" ? "⚠️" : "ℹ️"}</div>
            {ergebnis.typ === "ok" && <div style={{ fontSize: 20, fontWeight: 700 }}>Übermittelt</div>}
            {ergebnis.nr && <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 0.5, margin: "6px 0" }}>{ergebnis.nr}</div>}
            <div style={{ color: THEME.colors.textSecondary }}>{ergebnis.text}</div>
          </div>
        )}
        {wartend > 0 && (
          <div style={{ color: THEME.colors.warning, marginBottom: 14 }}>⏳ {wartend} Beleg{wartend === 1 ? "" : "e"} wartet auf Netz. <button onClick={warteschlangeSenden} style={{ background: "none", border: "none", color: THEME.colors.primary, textDecoration: "underline", fontSize: "inherit", cursor: "pointer" }}>Jetzt senden</button></div>
        )}

        {letzte.length > 0 && (
          <div>
            <div style={{ color: THEME.colors.textSecondary, margin: "6px 0 8px" }}>Zuletzt übermittelt</div>
            {letzte.slice(0, 8).map(b => (
              <div key={b.beleg_nr} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
                <span>{b.beleg_nr}</span><span style={{ color: THEME.colors.textSecondary }}>{zeitText(b.zeit)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ color: THEME.colors.textMuted, fontSize: 13, textAlign: "center", marginTop: 24 }}>Eingerichtet als „{bezeichnung}“</div>
      </div>
    </div>
  );
}
